import axios from 'axios'
import {
  AccountInterface,
  AccountShipmentDto,
  AccountSiteInterfases,
  BusinessUnitInterface,
  DeliveryEnum,
  DestinationDto,
  DestinationInterface,
  FavAndHistoricalDestinationListInterface,
  Nullable,
  PaymentMethodAccountDto,
  RecurrentDestinationInterface,
  TaxIdentificationTypeInterface,
} from '../interfaces'
import { randomAccount } from '../utils/random'
import { ListResponse } from '../interfaces/Shipment/ListResponseDto'
import { NavigationItem } from '../components'
import { accountMapper, businessUnitMapper } from '../utils/mappers'
import { apiDjango } from './ApiDjango'
import { FavoriteAndRecentDestinationDjangoDto } from '../interfaces/Django/CatalogDjangoDto'
const { REACT_APP_URL_PATH_API, REACT_APP_URL_PATH_API_DJANGO } = process.env

export const getTaxIdentificationTypes = async (): Promise<
  TaxIdentificationTypeInterface[]
> => {
  //console.log(`[API CALL] GET Tax Identification Types`);
  return [
    {
      taxIdentificationTypeId: 1,
      taxIdentificationTypeCode: '001',
      taxIdentificationTypeName: 'Cedula Identidad',
      taxIdentificationTypeRegExp: '[V|E]{1-8}',
      countryId: 236,
      isJuridic: true,
      abbreviationName: 'V-',
    },
    {
      taxIdentificationTypeId: 2,
      taxIdentificationTypeCode: '002',
      taxIdentificationTypeName: 'Pasaporte',
      taxIdentificationTypeRegExp: '{1-9}',
      countryId: 236,
      isJuridic: true,
      abbreviationName: 'P-',
    },
    {
      taxIdentificationTypeId: 3,
      taxIdentificationTypeCode: '003',
      taxIdentificationTypeName: 'Gubernamental',
      taxIdentificationTypeRegExp: '[G]{1-8}',
      countryId: 236,
      isJuridic: false,
      abbreviationName: 'G-',
    },
    {
      taxIdentificationTypeId: 4,
      taxIdentificationTypeCode: '004',
      taxIdentificationTypeName: 'RIF',
      taxIdentificationTypeRegExp: '[J|G|V|E]-{1-8}-{0}',
      countryId: 236,
      isJuridic: false,
      abbreviationName: 'J-',
    },
    {
      taxIdentificationTypeId: 5,
      taxIdentificationTypeCode: '005',
      taxIdentificationTypeName: 'Extranjero',
      taxIdentificationTypeRegExp: '[E]{1-8}',
      countryId: 236,
      isJuridic: true,
      abbreviationName: 'E-',
    },
  ]
}

export const getAccounts = async (): Promise<AccountInterface[]> => {
  //console.log(`[API CALL] GET Accounts`);

  return new Array(Math.floor(Math.random() * 100) + 20)
    .fill(0)
    .map(() => randomAccount())
}

export const getAccount = async (
  accountId: string
): Promise<Nullable<AccountInterface>> => {
  //const uri = `${baseURLJsonServer}/accounts?id=${accountId}`;
  if (!accountId) {
    return null
  }

  const uri = `${REACT_APP_URL_PATH_API}/Account/GetAccount?accountId=${accountId}`

  try {
    const response = await axios.get(uri)
    if (response.status === 200) {
      return accountMapper(response.data)
    }
  } catch (error) {}

  return null
}

export const getFilteredAccounts = async (
  criteria: string
): Promise<Nullable<AccountInterface[]>> => {
  const uri = `${REACT_APP_URL_PATH_API}/Account?idStr=${criteria}&maxRows=20&withAgreementAuths=true`
  try {
    const response = await axios.get(uri)

    if (response.status === 200) {
      return response.data
    }
  } catch (error) {}

  return null
}

export const getFavoriteAndHistoricalLocations = async (
  accountId: string,
  taxIdentificationTypes: TaxIdentificationTypeInterface[],
  buList: BusinessUnitInterface[],
  search?: string
): Promise<ListResponse<FavAndHistoricalDestinationListInterface>> => {
  const uri = `${REACT_APP_URL_PATH_API}/GetFavoriteAndRecentDestinations?AccountID=${accountId}&SearchInput=${search}`

  try {
    const response = await axios.get(uri)
    if (
      response.status === 200 &&
      !response.data.didError &&
      !!response.data.model
    ) {
      const favoriteDestination = response.data
        .model as RecurrentDestinationInterface[]

      const favorite = favoriteDestination
        .filter((fav) => fav.tipoAdr === 'Favorito')
        .map((destination: RecurrentDestinationInterface) =>
          mapToDestiInterface(destination, taxIdentificationTypes, buList)
        )
      const Sales = favoriteDestination
        .filter((fav) => fav.tipoAdr === 'Sales')
        .map((destination: RecurrentDestinationInterface) =>
          mapToDestiInterface(destination, taxIdentificationTypes, buList)
        )

      return {
        message: '',
        didError: false,
        errorMessage: '',
        model: {
          accountID: '',
          favoriteDestinations: favorite,
          historicalDestinations: Sales,
        },
      }
    } else if (response.status === 200) {
      return response.data
    }

    return {
      model: null,
      didError: true,
      message: `Server status: ${response.status}`,
      errorMessage: 'Server error',
    }
  } catch (error) {
    return {
      model: null,
      didError: true,
      message: `Error: ${error}`,
      errorMessage: 'Application error',
    }
  }
}

/**
 * GET /api/account/destinations (Django) — destinos favoritos + recientes de la cuenta.
 * Drop-in de getFavoriteAndHistoricalLocations: misma firma y mismo retorno
 * ListResponse<FavAndHistoricalDestinationListInterface>, para que los consumidores solo
 * cambien el import. Adapta el DTO snake_case a RecurrentDestinationInterface y reusa
 * mapToDestiInterface (mismo mapeo a favoritos/recientes por source_type).
 * Envelope Django: se ramifica por `error`/`code`, nunca por el status HTTP (siempre 200).
 * PII: no loguear la respuesta.
 */
export const getFavoriteAndRecentDestinationsDjango = async (
  accountId: string,
  taxIdentificationTypes: TaxIdentificationTypeInterface[],
  buList: BusinessUnitInterface[],
  search?: string,
  lastSalesDays?: number
): Promise<ListResponse<FavAndHistoricalDestinationListInterface>> => {
  const toRecurrent = (
    d: FavoriteAndRecentDestinationDjangoDto
  ): RecurrentDestinationInterface => ({
    tipoAdr: d.source_type === 'Sales' ? 'Sales' : 'Favorito',
    // deliveryTypeID se compara con .toString() contra DeliveryEnum ('10'/'20').
    deliveryTypeID: String(
      d.delivery_type_id ?? ''
    ) as unknown as DeliveryEnum,
    buCode: d.bu_code ?? '',
    buAreaName: d.bu_area_name ?? '',
    latitude: d.latitude ?? 0,
    longitude: d.longitude ?? 0,
    addressID: d.address_id ?? '',
    addressName: d.address_name ?? '',
    addressLine1: d.address_line1 ?? '',
    addressLandMark: d.address_land_mark ?? '',
    postalCode: d.postal_code ?? '',
    consigneeAccountID: d.consignee_account_id ?? '',
    consigneeFullName: d.consignee_full_name ?? '',
    consigneePhone: d.consignee_phone ?? '',
    consigneeIdentificationType: d.consignee_identification_type ?? 0,
    consigneeIdentificationNumber: d.consignee_identification_number ?? '',
    creationDate: d.creation_date ?? '',
    consigneeEmail: d.consignee_email ?? '',
    displayName: d.display_name ?? '',
  })

  try {
    const resp = await apiDjango.get(
      `${REACT_APP_URL_PATH_API_DJANGO}/account/destinations`,
      {
        params: {
          account_id: accountId,
          ...(search ? { search_value: search } : {}),
          ...(lastSalesDays != null ? { last_sales_days: lastSalesDays } : {}),
        },
      }
    )
    if (resp.status === 200 && resp.data && resp.data.error === false) {
      const rows = ((resp.data.data ?? []) as FavoriteAndRecentDestinationDjangoDto[]).map(
        toRecurrent
      )
      const favorite = rows
        .filter((r) => r.tipoAdr === 'Favorito')
        .map((r) => mapToDestiInterface(r, taxIdentificationTypes, buList))
      const sales = rows
        .filter((r) => r.tipoAdr === 'Sales')
        .map((r) => mapToDestiInterface(r, taxIdentificationTypes, buList))
      return {
        didError: false,
        message: resp.data.message ?? '',
        errorMessage: '',
        model: {
          accountID: accountId,
          favoriteDestinations: favorite,
          historicalDestinations: sales,
        },
      }
    }
    const m = resp.data?.message || 'No se pudieron obtener los destinos favoritos'
    return { model: null, didError: true, message: m, errorMessage: m }
  } catch (error) {
    const m =
      (error as any)?.response?.data?.message ||
      'No se pudieron obtener los destinos favoritos'
    return { model: null, didError: true, message: m, errorMessage: m }
  }
}

// Method to map DestinationDto to DestinationInterface
export const mapToDestiInterface = (
  destination: RecurrentDestinationInterface,
  taxIdTypes: TaxIdentificationTypeInterface[],
  buList: BusinessUnitInterface[]
): DestinationInterface => {
  const taxIdType = taxIdTypes.find(
    (taxIdType) =>
      taxIdType.taxIdentificationTypeId ===
      destination.consigneeIdentificationType
  )

  let businessUnit: BusinessUnitInterface

  if (destination.deliveryTypeID.toString() === DeliveryEnum.OFFICE) {
    businessUnit = buList.find((bu) => bu.buCode === destination.buCode)!
  } else {
    businessUnit = {} as BusinessUnitInterface
  }

  return {
    account: {
      id: destination.consigneeAccountID,
      //accountCode: destination.accountCode,
      taxIdentificationTypeID: destination.consigneeIdentificationType,
      identificationNumber: destination.consigneeIdentificationNumber,
      accountFullName: destination.consigneeFullName,
      taxIdentificationTypeCode: taxIdType?.taxIdentificationTypeCode ?? '',
      abbreviationName: taxIdType?.abbreviationName ?? '',
      displayName: destination.displayName ?? '',
      typeSearch: destination.tipoAdr,
      listAccountPhone: destination.consigneePhone
        ? [
            {
              phoneID: '', // No disponible en RecurrentDestinationInterface
              accountId: destination.consigneeAccountID || '',
              countryId: '', // No disponible en RecurrentDestinationInterface
              phoneNumber: destination.consigneePhone || '',
              countryPhoneAccessCode: '', // No disponible en RecurrentDestinationInterface
              phoneTypeId: '', // No disponible en RecurrentDestinationInterface
            },
          ]
        : [],
      listAccountEmail: destination.consigneeEmail
        ? [
            {
              emailID: '',
              accountId: destination.consigneeAccountID || '',
              email: destination.consigneeEmail,
            },
          ]
        : [],
      listAuthorizingAccount: [],
      creationDate: '',
      creationUser: '',
    },
    businessUnit: businessUnit ?? {},
    location: {
      name: destination.addressName,
      address: destination.addressLine1,
      reference: destination.addressLandMark,
      postalCode: destination.postalCode,
      coordinates: {
        lat: destination.latitude ?? 0,
        lng: destination.longitude ?? 0,
      },
    },
    store: {
      id:
        buList.find((x) => x.buCode === destination.buCode)?.buid.toString() ??
        '',
      name: destination.buAreaName,
      code: destination.buCode,
      location: {
        name: destination.addressName ?? '',
        address: destination.addressLine1 ?? '',
        reference: destination.addressLandMark ?? '',
        postalCode: destination.postalCode ?? '',
        coordinates: {
          lat: destination.latitude ?? 0,
          lng: destination.longitude ?? 0,
        },
      },
      polygonList: [],
    },
    isHomeDelivery:
      destination.deliveryTypeID.toString() === DeliveryEnum.DELIVERY,
  }
}

// Method to map DestinationDto to DestinationInterface
export const mapToDestinationInterface = (
  destination: DestinationDto,
  taxIdTypes: TaxIdentificationTypeInterface[]
): DestinationInterface => {
  const taxIdType = taxIdTypes.find(
    (taxIdType) =>
      taxIdType.taxIdentificationTypeId === destination.taxIdentificationTypeID
  )
  return {
    account: {
      id: destination.accountID,
      accountCode: destination.accountCode,
      taxIdentificationTypeID: destination.taxIdentificationTypeID,
      identificationNumber: destination.identificationNumber,
      accountFullName: destination.accountFullName,
      displayName: destination.displayName ?? '',
      taxIdentificationTypeCode: taxIdType?.taxIdentificationTypeCode ?? '',
      abbreviationName: taxIdType?.abbreviationName ?? '',
      listAccountPhone: destination.accountPhoneList.map((phone) => ({
        phoneID: phone.accountPhoneID,
        accountID: destination.accountID,
        countryID: phone.accountPhoneCountryID,
        phoneNumber: phone.accountPhoneNumber,
        countryPhoneAccessCode: phone.accountCountryPhoneAccessCode,
        phoneTypeID: phone.accountPhoneTypeID,
      })),
      listAccountEmail:
        destination.accountEmailList?.map((email) => ({
          email: email.accountEmail,
          accountID: destination.accountID,
          emailID: email.accountEmailID,
          emailTypeId: email.accountEmailTypeID,
        })) ?? [],
      listAuthorizingAccount: [],
      creationDate: '',
      creationUser: '',
    },
    businessUnit: businessUnitMapper(destination.businessUnit),
    location: {
      name: destination.addressName,
      address:
        destination.addressLine1 +
        (destination.addressLine2 !== null
          ? ' ' + destination.addressLine2
          : ''),
      reference: destination.addressReference,
      postalCode: destination.postalCode,
      coordinates: {
        lat: destination.latitude ?? 0,
        lng: destination.longitude ?? 0,
      },
    },
    store: {
      id: destination.businessUnit?.buid.toString(),
      name: destination.businessUnit?.buName,
      code: destination.businessUnit?.buCode,
      location: {
        name: destination.businessUnit?.buAreaName ?? '',
        address: destination.businessUnit?.address ?? '',
        reference: destination.businessUnit?.reference ?? '',
        postalCode: destination.businessUnit?.postalCode ?? '',
        coordinates: {
          lat: !!destination.businessUnit?.latitude
            ? parseFloat(destination.businessUnit?.latitude)
            : 0,
          lng: !!destination.businessUnit?.longitude
            ? parseFloat(destination.businessUnit?.longitude)
            : 0,
        },
      },
      polygonList: [],
    },
    isHomeDelivery: destination.isHomeDelivery,
  }
}

export const GetModuleByUser = async (
  id?: string,
  roleID?: number
): Promise<NavigationItem[]> => {
  const uri = `${REACT_APP_URL_PATH_API}/Users/GetModuleByUser?UserID=${id}&RoleID=${roleID}&applicationCode=${'020'}`
  try {
    const response = await axios.get(uri, {
      headers: {
        'Content-Type': 'application/json',
      },
    })
    if (response.status === 200) {
      const responseModule = response.data
      let modules: NavigationItem[] = responseModule
      return modules
    }
  } catch (error) {
    console.log('ha ocurrido un error', error)
  }

  return []
}

export const getAccounSiteByUser = async (
  id?: string
): Promise<AccountInterface[]> => {
  const uri = `${REACT_APP_URL_PATH_API}/Users/GetAccounSiteByUser?UserID=${id}`
  try {
    const response = await axios.get(uri, {
      headers: {
        'Content-Type': 'application/json',
      },
    })
    if (response.status === 200) {
      const responseModule = response.data
      if (responseModule.model.length === 0) {
        return []
      }
      let AccounstSite: AccountInterface[] = []
      responseModule.model.forEach((item: any) =>
        AccounstSite.push({
          id: item.accountID as string,
          accountCode: item.accountSiteCode,
          BuCodeSource: item.buCodeSource,
          cityName: item.cityName,
          cityCode: item.cityCode,
          serviceID: item.serviceID ?? 0,
          abbreviationName: '',
          identificationNumber: '',
          accountFullName: item.accountSiteName,
          accountSiteName: item.accountSiteName,
          listAccountPhone: [],
          listAccountEmail: [],
          listAuthorizingAccount: [],
          accountSiteID: item.accountSiteID,
          accountBillToId: item.accountBillTo,
          displayName: item.displayName,
          accountShortName: item.accountShortName,
        })
      )
      return AccounstSite
      //return modules;
    }
  } catch (error) {
    console.log('ha ocurrido un error', error)
  }

  return []
}

export const GetAccountSiteByUser = async (
  accountID?: string,
  accountSiteId?: number
): Promise<ListResponse<AccountSiteInterfases[]>> => {
  const uri = `${REACT_APP_URL_PATH_API}/account/GetAccountSite?AccountID=${accountID}&AccountSiteId=${accountSiteId}`
  var response = await axios
    .get(uri)
    .then(function (response) {
      var responsedto = response.data
      return responsedto as ListResponse<AccountSiteInterfases[]>
    })
    .catch(function (error) {
      if (error.response) {
        console.log(error.message ?? '')
        return error.response as ListResponse<AccountSiteInterfases[]>
      } else {
        console.log(error.message ?? '')
      }
      return [] as unknown as ListResponse<AccountSiteInterfases[]>
    })

  return response
}

export const UpdateAccountShipment = async (
  account: AccountShipmentDto
): Promise<ListResponse<AccountShipmentDto>> => {
  var response = await axios
    .post(`${REACT_APP_URL_PATH_API}/Account/UpdateShipment`, account)
    .then(function (response) {
      var data = response.data as ListResponse<AccountShipmentDto>
      return data
    })
    .catch(function (error) {
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        let errorData = error.response.data as ListResponse<AccountShipmentDto>
        console.log('Error actualizando cliente:', errorData.errorMessage)
        return errorData
      } else {
        // Something happened in setting up the request that triggered an Error
        console.log('Error actualizando cliente:', error.message)
      }
      return [] as unknown as ListResponse<AccountShipmentDto>
    })
  return response
}

export const GetPaymentMethodAccount = async (
  accountID?: string
): Promise<ListResponse<PaymentMethodAccountDto[]>> => {
  const uri = `${REACT_APP_URL_PATH_API}/account/GetAllPaymentMethodAccount?AccountID=${accountID}`
  var response = await axios
    .get(uri)
    .then(function (response) {
      var responsedto = response.data
      return responsedto as ListResponse<PaymentMethodAccountDto[]>
    })
    .catch(function (error) {
      if (error.response) {
        console.log(error.message ?? '')
        return error.response as ListResponse<PaymentMethodAccountDto[]>
      } else {
        console.log(error.message ?? '')
      }
      return [] as unknown as ListResponse<PaymentMethodAccountDto[]>
    })

  return response
}

export const saveAccountService = async (
  account: AccountInterface
): Promise<ListResponse<AccountInterface>> => {
  account.creationDate = new Date().toISOString()
  const uri = `${REACT_APP_URL_PATH_API}/Account`

  try {
    var response = await axios.post(uri, account)
    if (response.status === 200) {
      return response.data
    }
    return {
      model: null,
      didError: true,
      message: `Server status: ${response.status}`,
      errorMessage: 'Server error',
    }
  } catch (error: any) {
    console.log(error)
    return {
      statusId: error?.response.status,
      model: null,
      didError: true,
      message: `Error: ${error}`,
      errorMessage: 'Application',
    }
  }
}
